import * as React from 'react'
import { createPortal } from 'react-dom'
import {
  FieldArrayWithId,
  Control,
  FieldErrors,
  UseFormRegister,
} from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/free-solid-svg-icons'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import { ModalWrapper } from '~/atoms/modal/ModalWrapper'

import { Button } from '~/atoms/button/Button'
import { InputNumber } from '~/atoms/form/InputNumber'
import { InputDate } from '~/atoms/form/InputDate'
import { InputSelect } from '~/atoms/form/InputSelect'
import { InputTextArea } from '~/atoms/form/InputTextArea'
import { InformationText } from '~/atoms/text/InformationText'
import { Toast } from '~/atoms/popup/Toast'
import { ProgressStep } from '~/atoms/progess/ProgressStep'
import { StepProgress } from '~/molecules/progress/StepProgress'
import { FormFooterAction } from '~/molecules/form/FormFooterAction'
import { ReserveOption } from '~/organisms/reserve/ReserveOption'

import * as Style from './style.css'

export interface IProps {
  isInitialized?: boolean
  isTokyoBsWaiting?: boolean
  isReportBack?: boolean
  fields: FieldArrayWithId<ICompleteReportFormStateStep1, 'options', 'id'>[]
  inputStartDay: Date
  inputEndDay: Date
  inputOptions: IReserveOption[]
  inputAdditionalChildOption: IAdditionalChildOptionState
  inputTransportationPrice?: number
  showToast?: boolean
  showError?: boolean
  showConfirm?: boolean
  errorMessages?: string[]
  showOptionAlert?: boolean
  priceDetailHtml?: string
  control: Control<ICompleteReportFormStateStep1>
  errors: FieldErrors<ICompleteReportFormStateStep1>
  register: UseFormRegister<ICompleteReportFormStateStep1>
  submitHandler: () => void
  cancelHandler: () => void
  clickTmpSaveHandler: () => void
  addOptionHandler: () => void
  deleteOptionHandler: (index: number) => void
  changePriceHandler: () => void
  confirmApproveHandler: () => void
  confirmCancelHandler: () => void
}

export const SittingReportStep1 = ({
  isInitialized,
  isTokyoBsWaiting,
  isReportBack,
  fields,
  inputStartDay,
  inputEndDay,
  inputAdditionalChildOption,
  inputOptions,
  inputTransportationPrice,
  showToast,
  showError,
  showConfirm,
  errorMessages,
  showOptionAlert,
  priceDetailHtml,
  control,
  errors,
  register,
  submitHandler,
  cancelHandler,
  clickTmpSaveHandler,
  addOptionHandler,
  deleteOptionHandler,
  changePriceHandler,
  confirmApproveHandler,
  confirmCancelHandler,
}: IProps): JSX.Element => {
  return (
    <div className={Style.SittingReportStep1}>
      <div className={Style.SittingReportStep1Progress}>
        <StepProgress>
          <ProgressStep isActive={true}>サポート情報</ProgressStep>
          <ProgressStep>レポート情報</ProgressStep>
          <ProgressStep>入力内容確認</ProgressStep>
        </StepProgress>
      </div>
      <h1 className={Style.SittingReportStep1Title}>
        STEP1. サポート情報の入力
      </h1>
      <form onSubmit={submitHandler}>
        <div className="form-block form-style-normal">
          <div className={Style.SittingReportStep1FormSection}>
            <div
              id="edit-support-date"
              className={Style.SittingReportStep1FormSectionTitle}
            >
              サポート日時
            </div>
            <div>
              <p className={Style.SittingReportStep1FormSectionDescription}>
                サポート時間が予約確定時と異なる場合は、修正してください。理由は完了報告コメントにご記入ください。
              </p>
              <div
                className={Style.SittingReportStep1FormSectionDescriptionInfo}
              >
                <InformationText
                  title="サポート時間より延長した場合は延長料金(20%)が発生します。また、サポート時間の短縮はサポーター都合の場合のみ行えます。ユーザー都合につきましては規約上行えない旨をお伝えし予定のサポート時間通りに完了報告を行いましょう。"
                  color="notice"
                />
              </div>
              <div className={Style.SittingReportStep1FormRowWithBorder}>
                <div className={Style.SittingReportStep1FormRowTitle}>
                  <p className={Style.SittingReportStep1FormRowTitleText}>
                    開始日時
                  </p>
                </div>
                <div className={Style.SittingReportStep1FormInputDateTime}>
                  <div className={Style.SittingReportStep1FormInputDateTimeDay}>
                    <InputDate
                      defaultDate={inputStartDay}
                      readOnly={true}
                      control={control}
                      {...register('startDay')}
                    />
                  </div>
                  <div>
                    <InputSelect
                      {...register('startHour', {
                        onChange: changePriceHandler,
                      })}
                    >
                      <option value="00">00</option>
                      <option value="01">01</option>
                      <option value="02">02</option>
                      <option value="03">03</option>
                      <option value="04">04</option>
                      <option value="05">05</option>
                      <option value="06">06</option>
                      <option value="07">07</option>
                      <option value="08">08</option>
                      <option value="09">09</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="13">13</option>
                      <option value="14">14</option>
                      <option value="15">15</option>
                      <option value="16">16</option>
                      <option value="17">17</option>
                      <option value="18">18</option>
                      <option value="19">19</option>
                      <option value="20">20</option>
                      <option value="21">21</option>
                      <option value="22">22</option>
                      <option value="23">23</option>
                    </InputSelect>{' '}
                    :{' '}
                    <InputSelect
                      {...register('startMinute', {
                        onChange: changePriceHandler,
                      })}
                      withIcon={true}
                    >
                      <option value="00">00</option>
                      <option value="01">01</option>
                      <option value="02">02</option>
                      <option value="03">03</option>
                      <option value="04">04</option>
                      <option value="05">05</option>
                      <option value="06">06</option>
                      <option value="07">07</option>
                      <option value="08">08</option>
                      <option value="09">09</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="13">13</option>
                      <option value="14">14</option>
                      <option value="15">15</option>
                      <option value="16">16</option>
                      <option value="17">17</option>
                      <option value="18">18</option>
                      <option value="19">19</option>
                      <option value="20">20</option>
                      <option value="21">21</option>
                      <option value="22">22</option>
                      <option value="23">23</option>
                      <option value="24">24</option>
                      <option value="25">25</option>
                      <option value="26">26</option>
                      <option value="27">27</option>
                      <option value="28">28</option>
                      <option value="29">29</option>
                      <option value="30">30</option>
                      <option value="31">31</option>
                      <option value="32">32</option>
                      <option value="33">33</option>
                      <option value="34">34</option>
                      <option value="35">35</option>
                      <option value="36">36</option>
                      <option value="37">37</option>
                      <option value="38">38</option>
                      <option value="39">39</option>
                      <option value="40">40</option>
                      <option value="41">41</option>
                      <option value="42">42</option>
                      <option value="43">43</option>
                      <option value="44">44</option>
                      <option value="45">45</option>
                      <option value="46">46</option>
                      <option value="47">47</option>
                      <option value="48">48</option>
                      <option value="49">49</option>
                      <option value="50">50</option>
                      <option value="51">51</option>
                      <option value="52">52</option>
                      <option value="53">53</option>
                      <option value="54">54</option>
                      <option value="55">55</option>
                      <option value="56">56</option>
                      <option value="57">57</option>
                      <option value="58">58</option>
                      <option value="59">59</option>
                    </InputSelect>
                  </div>
                </div>
              </div>
              <div className={Style.SittingReportStep1FormRowWithBorder}>
                <div className={Style.SittingReportStep1FormRowTitle}>
                  <p className={Style.SittingReportStep1FormRowTitleText}>
                    終了日時
                  </p>
                </div>
                <div className={Style.SittingReportStep1FormInputDateTime}>
                  <div className={Style.SittingReportStep1FormInputDateTimeDay}>
                    <InputDate
                      defaultDate={inputEndDay}
                      minDate={inputStartDay}
                      control={control}
                      {...register('endDay', { onChange: changePriceHandler })}
                    />
                  </div>
                  <div>
                    <InputSelect
                      {...register('endHour', { onChange: changePriceHandler })}
                    >
                      <option value="00">00</option>
                      <option value="01">01</option>
                      <option value="02">02</option>
                      <option value="03">03</option>
                      <option value="04">04</option>
                      <option value="05">05</option>
                      <option value="06">06</option>
                      <option value="07">07</option>
                      <option value="08">08</option>
                      <option value="09">09</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="13">13</option>
                      <option value="14">14</option>
                      <option value="15">15</option>
                      <option value="16">16</option>
                      <option value="17">17</option>
                      <option value="18">18</option>
                      <option value="19">19</option>
                      <option value="20">20</option>
                      <option value="21">21</option>
                      <option value="22">22</option>
                      <option value="23">23</option>
                    </InputSelect>{' '}
                    :{' '}
                    <InputSelect
                      {...register('endMinute', {
                        onChange: changePriceHandler,
                      })}
                      withIcon={true}
                    >
                      <option value="00">00</option>
                      <option value="01">01</option>
                      <option value="02">02</option>
                      <option value="03">03</option>
                      <option value="04">04</option>
                      <option value="05">05</option>
                      <option value="06">06</option>
                      <option value="07">07</option>
                      <option value="08">08</option>
                      <option value="09">09</option>
                      <option value="10">10</option>
                      <option value="11">11</option>
                      <option value="12">12</option>
                      <option value="13">13</option>
                      <option value="14">14</option>
                      <option value="15">15</option>
                      <option value="16">16</option>
                      <option value="17">17</option>
                      <option value="18">18</option>
                      <option value="19">19</option>
                      <option value="20">20</option>
                      <option value="21">21</option>
                      <option value="22">22</option>
                      <option value="23">23</option>
                      <option value="24">24</option>
                      <option value="25">25</option>
                      <option value="26">26</option>
                      <option value="27">27</option>
                      <option value="28">28</option>
                      <option value="29">29</option>
                      <option value="30">30</option>
                      <option value="31">31</option>
                      <option value="32">32</option>
                      <option value="33">33</option>
                      <option value="34">34</option>
                      <option value="35">35</option>
                      <option value="36">36</option>
                      <option value="37">37</option>
                      <option value="38">38</option>
                      <option value="39">39</option>
                      <option value="40">40</option>
                      <option value="41">41</option>
                      <option value="42">42</option>
                      <option value="43">43</option>
                      <option value="44">44</option>
                      <option value="45">45</option>
                      <option value="46">46</option>
                      <option value="47">47</option>
                      <option value="48">48</option>
                      <option value="49">49</option>
                      <option value="50">50</option>
                      <option value="51">51</option>
                      <option value="52">52</option>
                      <option value="53">53</option>
                      <option value="54">54</option>
                      <option value="55">55</option>
                      <option value="56">56</option>
                      <option value="57">57</option>
                      <option value="58">58</option>
                      <option value="59">59</option>
                    </InputSelect>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {isTokyoBsWaiting && inputAdditionalChildOption && (
            <div className={Style.SittingReportStep1FormSection}>
              <div className={Style.SittingReportStep1FormSectionTitle}>
                追加でお預りしたお子様の帰宅時刻
              </div>
              <div>
                <p className={Style.SittingReportStep1FormSectionDescription}>
                  追加でお預りしたお子様の帰宅時刻が予定時刻と異なる場合は、修正してください。
                </p>
                <div className={Style.SittingReportStep1FormRow}>
                  <div className={Style.SittingReportStep1FormRowTitle}>
                    <p className={Style.SittingReportStep1FormRowTitleText}>
                      帰宅日時
                    </p>
                  </div>
                  <div className={Style.SittingReportStep1FormInputDateTime}>
                    <div>
                      <InputSelect
                        {...register('additionalChildOption.hour', {
                          onChange: changePriceHandler,
                        })}
                      >
                        <option value="00">00</option>
                        <option value="01">01</option>
                        <option value="02">02</option>
                        <option value="03">03</option>
                        <option value="04">04</option>
                        <option value="05">05</option>
                        <option value="06">06</option>
                        <option value="07">07</option>
                        <option value="08">08</option>
                        <option value="09">09</option>
                        <option value="10">10</option>
                        <option value="11">11</option>
                        <option value="12">12</option>
                        <option value="13">13</option>
                        <option value="14">14</option>
                        <option value="15">15</option>
                        <option value="16">16</option>
                        <option value="17">17</option>
                        <option value="18">18</option>
                        <option value="19">19</option>
                        <option value="20">20</option>
                        <option value="21">21</option>
                        <option value="22">22</option>
                        <option value="23">23</option>
                      </InputSelect>{' '}
                      :{' '}
                      <InputSelect
                        {...register('additionalChildOption.minute', {
                          onChange: changePriceHandler,
                        })}
                        withIcon={true}
                      >
                        <option value="00">00</option>
                        <option value="01">01</option>
                        <option value="02">02</option>
                        <option value="03">03</option>
                        <option value="04">04</option>
                        <option value="05">05</option>
                        <option value="06">06</option>
                        <option value="07">07</option>
                        <option value="08">08</option>
                        <option value="09">09</option>
                        <option value="10">10</option>
                        <option value="11">11</option>
                        <option value="12">12</option>
                        <option value="13">13</option>
                        <option value="14">14</option>
                        <option value="15">15</option>
                        <option value="16">16</option>
                        <option value="17">17</option>
                        <option value="18">18</option>
                        <option value="19">19</option>
                        <option value="20">20</option>
                        <option value="21">21</option>
                        <option value="22">22</option>
                        <option value="23">23</option>
                        <option value="24">24</option>
                        <option value="25">25</option>
                        <option value="26">26</option>
                        <option value="27">27</option>
                        <option value="28">28</option>
                        <option value="29">29</option>
                        <option value="30">30</option>
                        <option value="31">31</option>
                        <option value="32">32</option>
                        <option value="33">33</option>
                        <option value="34">34</option>
                        <option value="35">35</option>
                        <option value="36">36</option>
                        <option value="37">37</option>
                        <option value="38">38</option>
                        <option value="39">39</option>
                        <option value="40">40</option>
                        <option value="41">41</option>
                        <option value="42">42</option>
                        <option value="43">43</option>
                        <option value="44">44</option>
                        <option value="45">45</option>
                        <option value="46">46</option>
                        <option value="47">47</option>
                        <option value="48">48</option>
                        <option value="49">49</option>
                        <option value="50">50</option>
                        <option value="51">51</option>
                        <option value="52">52</option>
                        <option value="53">53</option>
                        <option value="54">54</option>
                        <option value="55">55</option>
                        <option value="56">56</option>
                        <option value="57">57</option>
                        <option value="58">58</option>
                        <option value="59">59</option>
                      </InputSelect>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className={Style.SittingReportStep1FormSection}>
            <div
              id="edit-price"
              className={Style.SittingReportStep1FormSectionTitle}
            >
              サポート費
            </div>
            <div>
              {showOptionAlert && (
                <div className={Style.SittingReportStep1OptionAlert}>
                  <InformationText
                    color="alert"
                    title="サポート時間が変更されています"
                  >
                    オプション料金や内容を確認のうえ、必要に応じて修正してください。自動計算はされませんのでご注意ください。
                  </InformationText>
                </div>
              )}
              {!isTokyoBsWaiting && (
                <div className={Style.SittingReportStep1FormRow}>
                  <div className={Style.SittingReportStep1FormRowTitle}>
                    <p className={Style.SittingReportStep1FormRowTitleText}>
                      オプション料金
                    </p>
                  </div>
                  <div className={Style.SittingReportStep1FormInput}>
                    <div
                      className={Style.SittingReportStep1FormInputOptionWrapper}
                    >
                      {fields?.map((_, index) => (
                        <div
                          key={`reserve-option-${index}`}
                          className={Style.SittingReportStep1FormInputOption}
                        >
                          <ReserveOption
                            index={index}
                            option={inputOptions[index]}
                            errors={errors}
                            register={register}
                            deleteHandler={deleteOptionHandler}
                            changePriceHandler={changePriceHandler}
                          />
                        </div>
                      ))}
                      <div>
                        <a
                          className={Style.SittingReportStep1FormInputOptionAdd}
                          onClick={addOptionHandler}
                        >
                          <FontAwesomeIcon
                            icon={faSquarePlus}
                            className={
                              Style.SittingReportStep1FormInputOptionAddIcon
                            }
                          />
                          オプションを追加する
                        </a>
                      </div>
                      <p
                        className={
                          Style.SittingReportStep1FormInputOptionDescription
                        }
                      >
                        原則、サポーター側でオプション料金を設定しているもの以外は提示しないでください。
                      </p>
                    </div>
                  </div>
                </div>
              )}
              <div className={Style.SittingReportStep1FormRow}>
                <div className={Style.SittingReportStep1FormRowTitle}>
                  <p className={Style.SittingReportStep1FormRowTitleText}>
                    交通費(往復)または経費
                  </p>
                </div>
                <div className={Style.SittingReportStep1FormInput}>
                  <div
                    className={Style.SittingReportStep1FormInputTransportation}
                  >
                    <div
                      className={
                        Style.SittingReportStep1FormInputTransportationPrice
                      }
                    >
                      <InputNumber
                        placeholder="往復の交通費または経費"
                        min={0}
                        {...register('transportationPrice', {
                          onBlur: changePriceHandler,
                        })}
                        unit="円"
                      />
                    </div>
                    <div>
                      <InputTextArea
                        rows={4}
                        placeholder="(内訳)"
                        isErrorAlert={!!errors.transportationPriceDescription}
                        {...register('transportationPriceDescription', {
                          required:
                            !!inputTransportationPrice &&
                            inputTransportationPrice > 0 &&
                            '「交通費(往復)または経費内訳」を入力して下さい',
                        })}
                      />
                    </div>
                    <div className={Style.SittingReportStep1FormInputError}>
                      <ErrorMessage
                        errors={errors}
                        name={`transportationPriceDescription`}
                        render={({ message }) => <p>{message}</p>}
                      />
                    </div>
                    <div
                      className={
                        Style.SittingReportStep1FormInputTransportationExample
                      }
                    >
                      <div className={Style.SittingReportStep1Example}>
                        <input
                          className={Style.SittingReportStep1ExampleCtrl}
                          id="transportationExample"
                          type="checkbox"
                        />
                        <label
                          className={Style.SittingReportStep1ExampleLabel}
                          htmlFor="transportationExample"
                        >
                          入力例
                        </label>
                        <div className={Style.SittingReportStep1ExamplePopup}>
                          <div
                            className={
                              Style.SittingReportStep1ExamplePopupClose
                            }
                          >
                            <label
                              className={
                                Style.SittingReportStep1ExamplePopupCloseIcon
                              }
                              htmlFor="transportationExample"
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </label>
                          </div>
                          <p className={Style.SittingReportStep1ExampleText}>
                            交通費：横浜から目黒までの往復330円
                            <br />
                            経費：材料費500円
                          </p>
                        </div>
                      </div>
                    </div>
                    <div
                      className={
                        Style.SittingReportStep1FormInputTransportationDescription
                      }
                    >
                      原則、最安ルートのICカード料金で計算してください。内訳には詳細な経路をご記入ください。
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {!isReportBack && (
            <>
              <div className="reserve-confirm-warning layout-margin-large">
                <div className="confirm-warning-title">
                  完了報告がユーザーの満足度を大きく左右します
                </div>
                <div className="confirm-warning-body">
                  完了報告では、お子様の様子、可能性を感じた部分を詳細に記入しましょう。
                </div>
                <div className="confirm-warning-link">
                  <a
                    href="https://kidsline.me/help/center_detail/support_report"
                    target="_blank"
                  >
                    上手な完了報告の書き方
                  </a>
                </div>
              </div>
              <div className="reserve-confirm-warning layout-margin-large">
                <div className="confirm-warning-title">
                  完了報告は当日中に送信しましょう
                </div>
                <div className="confirm-warning-body">
                  必ずサポート当日中に送信してください。完了報告の送信後、ユーザーが完了報告を承認しないとお支払いが行われません。
                </div>
              </div>
            </>
          )}
          {isReportBack && (
            <div className="reserve-confirm-warning layout-margin-large">
              <div className="confirm-warning-title">
                前回送信した完了報告は修正いただけません
              </div>
              <div className="confirm-warning-body">
                前回の完了報告内容の修正を求められた場合には、コメント欄に再度完了報告を記載するか変更点を記載してください。
              </div>
            </div>
          )}
        </div>

        <div dangerouslySetInnerHTML={{ __html: priceDetailHtml }}></div>

        <FormFooterAction
          cancelHandler={cancelHandler}
          clickTmpSaveHandler={clickTmpSaveHandler}
        />
      </form>
      {isInitialized &&
        createPortal(
          <>
            <Toast
              visible={showToast}
              title="一時保存しました"
              color="notice"
            ></Toast>
            <Toast
              visible={showError}
              title="保存できませんでした"
              color="alert"
            >
              {errorMessages?.map((message, index) => (
                <p key={`error-message-${index}`}>・{message}</p>
              ))}
            </Toast>
            <ModalWrapper
              animation="fadeIn"
              mobileAnimation="fadeIn"
              isOpen={showConfirm}
            >
              <div className={Style.SittingReportStep1Confirm}>
                <div className={Style.SittingReportStep1ConfirmContent}>
                  <p className={Style.SittingReportStep1ConfirmMessage}>
                    変更を破棄しますか？
                  </p>
                  <ul className={Style.SittingReportStep1ConfirmActions}>
                    <li>
                      <Button
                        type="button"
                        color="secondary"
                        onClick={confirmCancelHandler}
                      >
                        キャンセル
                      </Button>
                    </li>
                    <li>
                      <Button
                        type="button"
                        color="primary"
                        onClick={confirmApproveHandler}
                      >
                        破棄する
                      </Button>
                    </li>
                  </ul>
                </div>
              </div>
            </ModalWrapper>
          </>,
          document.body,
        )}
    </div>
  )
}
